import "./MarkdownEditor.css";
import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { EditCtx } from "./EditCtx";
import { processText } from "../../utils/learnUtils";
import {
  IconButton,
  Tooltip,
  TextField,
  Popover,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import FunctionsIcon from "@mui/icons-material/Functions";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CheckIcon from "@mui/icons-material/Check";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";

const PRESET_COLORS = [
  { label: "Red", value: "#e53935" },
  { label: "Blue", value: "#1e88e5" },
  { label: "Green", value: "#43a047" },
  { label: "Orange", value: "#fb8c00" },
  { label: "Purple", value: "#8e24aa" },
  { label: "Teal", value: "#00897b" },
  { label: "Pink", value: "#e91e63" },
  { label: "Brown", value: "#795548" },
];

// Common LaTeX symbols for quick insertion
const LATEX_SYMBOLS = [
  { label: "α", latex: "\\alpha" },
  { label: "β", latex: "\\beta" },
  { label: "γ", latex: "\\gamma" },
  { label: "δ", latex: "\\delta" },
  { label: "ε", latex: "\\epsilon" },
  { label: "θ", latex: "\\theta" },
  { label: "λ", latex: "\\lambda" },
  { label: "μ", latex: "\\mu" },
  { label: "π", latex: "\\pi" },
  { label: "σ", latex: "\\sigma" },
  { label: "τ", latex: "\\tau" },
  { label: "φ", latex: "\\phi" },
  { label: "ω", latex: "\\omega" },
  { label: "Δ", latex: "\\Delta" },
  { label: "Σ", latex: "\\Sigma" },
  { label: "Ω", latex: "\\Omega" },
  { label: "∞", latex: "\\infty" },
  { label: "±", latex: "\\pm" },
  { label: "≤", latex: "\\leq" },
  { label: "≥", latex: "\\geq" },
  { label: "≠", latex: "\\neq" },
  { label: "≈", latex: "\\approx" },
  { label: "∈", latex: "\\in" },
  { label: "∇", latex: "\\nabla" },
  { label: "∂", latex: "\\partial" },
  { label: "∫", latex: "\\int" },
  { label: "√", latex: "\\sqrt{}" },
  { label: "ℝ", latex: "\\mathbb{R}" },
];

// ── LaTeX paste preprocessing ─────────────────────────────────────────────

function findMatchingBrace(text, start) {
  let depth = 1;
  let i = start + 1;
  while (i < text.length) {
    const c = text[i];
    if (c === "\\") {
      i += 2;
      continue;
    }
    if (c === "{") depth++;
    else if (c === "}") {
      depth--;
      if (depth === 0) return i + 1;
    }
    i++;
  }
  return -1;
}

function transformLatexCommand(text, name, numArgs, handler) {
  const pat = new RegExp(
    "\\\\" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(?![a-zA-Z@])",
    "g",
  );
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = pat.exec(text)) !== null) {
    parts.push(text.slice(lastIndex, match.index));
    let j = match.index + match[0].length;
    if (j < text.length && text[j] === "*") j++;
    if (j < text.length && text[j] === "[") {
      const close = text.indexOf("]", j);
      if (close !== -1) j = close + 1;
    }
    const args = [];
    let ok = true;
    for (let a = 0; a < numArgs; a++) {
      while (j < text.length && (text[j] === " " || text[j] === "\t")) j++;
      if (j >= text.length || text[j] !== "{") {
        ok = false;
        break;
      }
      const end = findMatchingBrace(text, j);
      if (end === -1) {
        ok = false;
        break;
      }
      args.push(text.slice(j + 1, end - 1));
      j = end;
    }
    if (ok) {
      parts.push(handler(args));
      lastIndex = j;
      pat.lastIndex = j;
    } else {
      parts.push(text.slice(match.index, match.index + match[0].length));
      lastIndex = match.index + match[0].length;
      pat.lastIndex = lastIndex;
    }
  }
  parts.push(text.slice(lastIndex));
  return parts.join("");
}

function cleanLatexPaste(text) {
  const rm = (t, name, n) => transformLatexCommand(t, name, n, () => " ");

  // Strip line comments (% to end of line, respecting \%)
  text = text
    .split("\n")
    .map((line) => {
      let buf = "";
      for (let i = 0; i < line.length; i++) {
        if (line[i] === "\\" && i + 1 < line.length) {
          buf += line[i] + line[i + 1];
          i++;
          continue;
        }
        if (line[i] === "%") {
          // Preserve percent-encoded URL characters (%XX where XX are hex digits)
          if (
            i + 2 < line.length &&
            /^[0-9A-Fa-f]{2}/.test(line.slice(i + 1, i + 3))
          ) {
            buf += line[i];
            continue;
          }
          break;
        }
        buf += line[i];
      }
      return buf;
    })
    .join("\n");

  // Remove unwanted commands
  text = rm(text, "index", 1);
  text = rm(text, "marginAppendixB", 3);
  text = rm(text, "marginProblemBTwo", 3);
  text = rm(text, "marginProblemB", 2);
  text = rm(text, "marginNotebookB", 3);
  text = rm(text, "footnote", 1);
  text = rm(text, "vspace", 1);

  // \textcolor{color}{text} → color name
  text = transformLatexCommand(text, "textcolor", 2, (args) => args[1]);

  // {\color{name}...} → ...
  {
    const parts = [];
    let lastIndex = 0;
    const pat = /\{\s*\\color\s*\{[^{}]*\}\s*/g;
    let m;
    while ((m = pat.exec(text)) !== null) {
      parts.push(text.slice(lastIndex, m.index));
      const end = findMatchingBrace(text, m.index);
      if (end === -1) {
        parts.push(text.slice(m.index, m.index + m[0].length));
        lastIndex = m.index + m[0].length;
        pat.lastIndex = lastIndex;
      } else {
        parts.push(text.slice(m.index + m[0].length, end - 1));
        lastIndex = end;
        pat.lastIndex = end;
      }
    }
    parts.push(text.slice(lastIndex));
    text = parts.join("");
  }

  // {\em foo} → *foo*
  {
    const parts = [];
    let lastIndex = 0;
    const pat = /\{\\em\b\s*/g;
    let m;
    while ((m = pat.exec(text)) !== null) {
      parts.push(text.slice(lastIndex, m.index));
      const end = findMatchingBrace(text, m.index);
      if (end === -1) {
        parts.push(text.slice(m.index));
        lastIndex = text.length;
        break;
      }
      parts.push("*" + text.slice(m.index + m[0].length, end - 1) + "*");
      lastIndex = end;
      pat.lastIndex = end;
    }
    parts.push(text.slice(lastIndex));
    text = parts.join("");
  }

  // \emph{foo} / \textit{foo} → *foo*
  text = transformLatexCommand(text, "emph", 1, (args) => "*" + args[0] + "*");
  text = transformLatexCommand(
    text,
    "textit",
    1,
    (args) => "*" + args[0] + "*",
  );

  // \ref{foo} → \ref{foo}{x}
  text = text.replace(/\\ref\{([^{}]*)\}/g, "\\ref{$1}{x}");

  // \argmax / \argmin → \operatorname{...}
  text = text.replace(/\\argmax(?![a-zA-Z@])/g, "\\operatorname{argmax}");
  text = text.replace(/\\argmin(?![a-zA-Z@])/g, "\\operatorname{argmin}");

  // \ie → i.e.,  /  \eg → e.g.
  text = text.replace(/\\ie(?![a-zA-Z@])/g, "i.e.,");
  text = text.replace(/\\eg(?![a-zA-Z@])/g, "e.g.");

  // $...$. → $.....$ and $...$, → $...,$  (move trailing punct inside math)
  text = text.replace(/(?<![\\$])\$([^$\n]+?)\$([.,])(?!\$)/g, "$$$1$2$$");

  // ~ → space
  text = text.replace(/~/g, " ");

  // \noindent → ''
  text = text.replace(/\\noindent(?![a-zA-Z@])/g, "");

  // -- → -
  text = text.replace(/--/g, "-");

  // \% → $%$
  text = text.replace(/\\%/g, () => "$%$");

  return text;
}

/**
 * Markdown editor with live preview.
 *
 * Props:
 *   value       {string}   - controlled markdown string
 *   onChange    {function} - called with new string on every edit
 *   mode        {1|2}      - 1 = split (edit|preview side-by-side), 2 = toggle tabs
 *   supportItems {Array}   - support items from Redux for internal link autocomplete
 *   placeholder {string}   - textarea placeholder text
 *   height      {string}   - CSS height of the edit/preview area (default "400px")
 */
function MarkdownEditor({
  value = "",
  onChange,
  mode = 1,
  supportItems: propSupportItems,
  contentLabels: propContentLabels,
  placeholder = "Enter markdown here…",
  height = "400px",
}) {
  const { supportItems: ctxSupportItems, contentLabels: ctxContentLabels } =
    useContext(EditCtx);

  // Props take precedence; fall back to context (provided by EditContent).
  // Memoized so downstream useMemo hooks get stable references.
  const supportItems = useMemo(
    () => propSupportItems ?? ctxSupportItems ?? [],
    [propSupportItems, ctxSupportItems],
  );
  const contentLabels = useMemo(
    () => propContentLabels ?? ctxContentLabels ?? [],
    [propContentLabels, ctxContentLabels],
  );

  const textareaRef = useRef(null);
  // Last-known selection in the textarea, captured before focus leaves it.
  const selRef = useRef({ start: 0, end: 0 });

  // Mode-2 toggle state
  const [viewMode, setViewMode] = useState("edit");

  // Color picker
  const [colorAnchor, setColorAnchor] = useState(null);
  const [customColor, setCustomColor] = useState("#");
  const [copiedColor, setCopiedColor] = useState(null);

  // LaTeX symbol palette
  const [latexAnchor, setLatexAnchor] = useState(null);

  // External link dialog
  const [extOpen, setExtOpen] = useState(false);
  const [extUrl, setExtUrl] = useState("");
  const [extText, setExtText] = useState("");

  // Internal link dialog
  const [intOpen, setIntOpen] = useState(false);
  const [intSearch, setIntSearch] = useState("");
  const [intVariant, setIntVariant] = useState("regular");
  const [intDisplayText, setIntDisplayText] = useState("");
  const [intSelected, setIntSelected] = useState(null);

  // Copy-to-clipboard feedback
  const [copied, setCopied] = useState(false);

  // Ref / content-label link dialog
  const [refOpen, setRefOpen] = useState(false);
  const [refSearch, setRefSearch] = useState("");
  const [refSelected, setRefSelected] = useState(null);

  // ── Selection tracking ────────────────────────────────────────────
  // Capture the selection continuously so toolbar clicks (which move focus)
  // still know what the user had selected.
  const trackSel = (e) => {
    selRef.current = {
      start: e.target.selectionStart,
      end: e.target.selectionEnd,
    };
  };

  // ── Core text-mutation helpers ────────────────────────────────────

  /** Wrap the current selection (or `fallback`) with `before`…`after`. */
  const wrapSel = (before, after, fallback = "") => {
    const { start, end } = selRef.current;
    const sel = value.substring(start, end) || fallback;
    const next =
      value.substring(0, start) + before + sel + after + value.substring(end);
    onChange(next);
    // Restore focus and leave the inserted text selected
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(
          start + before.length,
          start + before.length + sel.length,
        );
        selRef.current = {
          start: start + before.length,
          end: start + before.length + sel.length,
        };
      }
    });
  };

  /** Replace the current selection with `replacement`. */
  const replaceSel = (replacement) => {
    const { start, end } = selRef.current;
    const next = value.substring(0, start) + replacement + value.substring(end);
    onChange(next);
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        const cur = start + replacement.length;
        ta.focus();
        ta.setSelectionRange(cur, cur);
        selRef.current = { start: cur, end: cur };
      }
    });
  };

  /** Insert `text` at cursor (no wrapping). */
  const insertAt = (text) => {
    const { start, end } = selRef.current;
    const next = value.substring(0, start) + text + value.substring(end);
    onChange(next);
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        const cur = start + text.length;
        ta.focus();
        ta.setSelectionRange(cur, cur);
        selRef.current = { start: cur, end: cur };
      }
    });
  };

  // ── Keyboard shortcuts ────────────────────────────────────────────
  const handleKeyDown = (e) => {
    // Always keep selection ref fresh while the textarea has focus
    selRef.current = {
      start: e.target.selectionStart,
      end: e.target.selectionEnd,
    };

    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case "b":
          e.preventDefault();
          wrapSel("**", "**", "bold text");
          break;
        case "i":
          e.preventDefault();
          wrapSel("*", "*", "italic text");
          break;
        case "m":
          e.preventDefault();
          wrapSel("$", "$", "x");
          break;
        default:
          break;
      }
    }

    // Tab → 2 spaces (keep focus in textarea)
    if (e.key === "Tab") {
      e.preventDefault();
      insertAt("  ");
    }
  };

  // ── Toolbar action handlers ───────────────────────────────────────

  const handleBold = () => wrapSel("**", "**", "bold text");
  const handleItalic = () => wrapSel("*", "*", "italic text");
  const handleInlineMath = () => wrapSel("$", "$", "x");
  const handleDisplayMath = () => wrapSel("$$\n", "\n$$", "x = y");

  const handleColorClick = (e) => {
    setCustomColor("#");
    setCopiedColor(null);
    setColorAnchor(e.currentTarget);
  };

  const applyColor = (color) => {
    const { start, end } = selRef.current;
    const sel = value.substring(start, end) || "colored text";
    replaceSel(`[${sel}](https://color:${color})`);
    setColorAnchor(null);
  };

  const handleLatexClick = (e) => {
    setLatexAnchor(e.currentTarget);
  };

  const applyLatex = (latex) => {
    // If it ends with {}, position cursor inside braces
    insertAt(latex);
    setLatexAnchor(null);
  };

  const handleExtClick = () => {
    const { start, end } = selRef.current;
    setExtText(value.substring(start, end));
    setExtUrl("https://");
    setExtOpen(true);
  };

  const applyExtLink = () => {
    const { start, end } = selRef.current;
    const text = extText || value.substring(start, end) || "link text";
    replaceSel(`[${text}](${extUrl})`);
    setExtOpen(false);
  };

  const handleIntClick = () => {
    const { start, end } = selRef.current;
    setIntDisplayText(value.substring(start, end));
    setIntSearch("");
    setIntSelected(null);
    setIntVariant("regular");
    setIntOpen(true);
  };

  const applyIntLink = () => {
    if (!intSelected) return;
    const text = intDisplayText || intSelected.name || "link";
    const id = intSelected._id;
    const urlKey =
      {
        regular: "internal-link",
        subtle: "internal-link2",
        italic: "internal-link-i",
        "subtle-italic": "internal-link2-i",
      }[intVariant] || "internal-link";
    replaceSel(`[${text}](https://${urlKey}/${id})`);
    setIntOpen(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text/plain");
    const cleaned = cleanLatexPaste(pasted);
    const start = e.target.selectionStart;
    const end = e.target.selectionEnd;
    const next = value.substring(0, start) + cleaned + value.substring(end);
    onChange(next);
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        const cur = start + cleaned.length;
        ta.focus();
        ta.setSelectionRange(cur, cur);
        selRef.current = { start: cur, end: cur };
      }
    });
  };

  // ── Ref / content-label link ──────────────────────────────────────
  const handleRefClick = () => {
    setRefSearch("");
    setRefSelected(null);
    setRefOpen(true);
  };

  const applyRefLink = () => {
    if (!refSelected) return;
    insertAt(`\\ref{${refSelected.label}}{0.0}`);
    setRefOpen(false);
  };

  const filteredLabels = useMemo(() => {
    if (!refSearch) return contentLabels;
    const q = refSearch.toLowerCase();
    return contentLabels.filter((item) => item.label.toLowerCase().includes(q));
  }, [refSearch, contentLabels]);

  // ── Filtered support items (autocomplete) ─────────────────────────
  const filteredItems = useMemo(() => {
    if (intSearch.length < 2) return supportItems;
    const q = intSearch.toLowerCase();
    return supportItems.filter(
      (item) =>
        (item.name || "").toLowerCase().includes(q) ||
        (item.keywords || []).some((k) => k.toLowerCase().includes(q)),
    );
  }, [intSearch, supportItems]);

  // ── MathJax re-typeset when preview changes ───────────────────────
  const previewHtml = processText(value);
  useEffect(() => {
    if (window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise().catch(() => {});
    }
  });

  // ── Textarea props (shared between split and toggle) ──────────────
  const textareaProps = {
    ref: textareaRef,
    className: "mde__textarea",
    value,
    onChange: (e) => onChange(e.target.value),
    onSelect: trackSel,
    onMouseUp: trackSel,
    onKeyUp: trackSel,
    onKeyDown: handleKeyDown,
    onPaste: handlePaste,
    placeholder,
    spellCheck: true,
  };

  const previewEl = (
    <div
      className="mde__preview"
      dangerouslySetInnerHTML={{ __html: previewHtml }}
    />
  );

  // ── Toolbar ───────────────────────────────────────────────────────
  const toolbar = (
    <div className="mde__toolbar">
      <Tooltip title="Bold  Ctrl+B">
        <IconButton size="small" onClick={handleBold}>
          <FormatBoldIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Italic  Ctrl+I">
        <IconButton size="small" onClick={handleItalic}>
          <FormatItalicIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <span className="mde__divider" />
      <Tooltip title="Inline math  $…$  Ctrl+M">
        <IconButton size="small" onClick={handleInlineMath}>
          <FunctionsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Display math  $$…$$">
        <IconButton
          size="small"
          onClick={handleDisplayMath}
          className="mde__display-math-btn"
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "serif",
              lineHeight: 1,
            }}
          >
            ∑
          </span>
        </IconButton>
      </Tooltip>
      <Tooltip title="LaTeX symbols">
        <IconButton
          size="small"
          onClick={handleLatexClick}
          style={{ fontSize: 11, fontFamily: "serif", fontWeight: 700 }}
        >
          <span>α</span>
        </IconButton>
      </Tooltip>
      <span className="mde__divider" />
      <Tooltip title="Text color">
        <IconButton size="small" onClick={handleColorClick}>
          <ColorLensIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="External link">
        <IconButton size="small" onClick={handleExtClick}>
          <InsertLinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Internal support link">
        <IconButton size="small" onClick={handleIntClick}>
          <MenuBookIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {contentLabels.length > 0 && (
        <Tooltip title="Insert content reference  \\ref{label}">
          <IconButton size="small" onClick={handleRefClick}>
            <BookmarkAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <span className="mde__spacer" />
      <Tooltip title={copied ? "Copied!" : "Copy markdown"}>
        <IconButton
          size="small"
          onClick={handleCopy}
          color={copied ? "success" : "default"}
        >
          {copied ? (
            <CheckIcon fontSize="small" />
          ) : (
            <ContentCopyIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="mde">
      {toolbar}

      {mode === 1 ? (
        <div className="mde__split" style={{ height }}>
          <div className="mde__split-pane">
            <div className="mde__pane-label">Markdown</div>
            <textarea {...textareaProps} />
          </div>
          <div className="mde__split-pane">
            <div className="mde__pane-label">Preview</div>
            {previewEl}
          </div>
        </div>
      ) : (
        <div className="mde__toggle">
          <div className="mde__toggle-tabs">
            <button
              className={`mde__tab${viewMode === "edit" ? " mde__tab--active" : ""}`}
              onClick={() => setViewMode("edit")}
            >
              <EditIcon style={{ fontSize: 13, marginRight: 4 }} />
              Edit
            </button>
            <button
              className={`mde__tab${viewMode === "preview" ? " mde__tab--active" : ""}`}
              onClick={() => setViewMode("preview")}
            >
              <VisibilityIcon style={{ fontSize: 13, marginRight: 4 }} />
              Preview
            </button>
          </div>
          <div className="mde__toggle-body" style={{ height }}>
            {viewMode === "edit" ? <textarea {...textareaProps} /> : previewEl}
          </div>
        </div>
      )}

      {/* ── Color picker popover ─────────────────────────────────── */}
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <div className="mde__color-picker">
          <Typography variant="caption" className="mde__picker-label">
            Preset colors
          </Typography>
          <div className="mde__swatches">
            {PRESET_COLORS.map((c) => (
              <Tooltip key={c.value} title={c.label}>
                <button
                  className="mde__swatch"
                  style={{ backgroundColor: c.value }}
                  onClick={() => applyColor(c.value)}
                />
              </Tooltip>
            ))}
          </div>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" className="mde__picker-label">
            Custom (name or #hex)
          </Typography>
          <div className="mde__color-row">
            <div
              className="mde__color-preview"
              style={{ backgroundColor: customColor }}
            />
            <TextField
              size="small"
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              placeholder="#ff0000"
              inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
              sx={{ flex: 1 }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => applyColor(customColor)}
            >
              Apply
            </Button>
          </div>
        </div>
      </Popover>

      {/* ── LaTeX symbol palette ─────────────────────────────────── */}
      <Popover
        open={Boolean(latexAnchor)}
        anchorEl={latexAnchor}
        onClose={() => setLatexAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <div className="mde__latex-palette">
          <Typography variant="caption" className="mde__picker-label">
            Insert LaTeX symbol (wraps in $…$)
          </Typography>
          <div className="mde__latex-grid">
            {LATEX_SYMBOLS.map((sym) => (
              <Tooltip key={sym.latex} title={sym.latex}>
                <button
                  className="mde__latex-btn"
                  onClick={() => applyLatex(`$${sym.latex}$`)}
                >
                  {sym.label}
                </button>
              </Tooltip>
            ))}
          </div>
        </div>
      </Popover>

      {/* ── External link dialog ─────────────────────────────────── */}
      <Dialog
        open={extOpen}
        onClose={() => setExtOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insert External Link</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "16px !important",
          }}
        >
          <TextField
            label="URL"
            value={extUrl}
            onChange={(e) => setExtUrl(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && applyExtLink()}
          />
          <TextField
            label="Display text (leave blank to keep selection)"
            value={extText}
            onChange={(e) => setExtText(e.target.value)}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyExtLink}
            disabled={!extUrl || extUrl === "https://"}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Internal link dialog ─────────────────────────────────── */}
      <Dialog
        open={intOpen}
        onClose={() => setIntOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insert Internal Support Link</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "16px !important",
          }}
        >
          <TextField
            label="Search by name or keyword"
            value={intSearch}
            onChange={(e) => setIntSearch(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            placeholder="Start typing to filter…"
          />

          {intSelected && (
            <Chip
              label={`Selected: ${intSelected.name}`}
              onDelete={() => setIntSelected(null)}
              color="primary"
              size="small"
            />
          )}

          <div className="mde__int-list">
            {filteredItems.length === 0 ? (
              <div className="mde__int-empty">No support items found</div>
            ) : (
              filteredItems.map((item) => (
                <div
                  key={item._id}
                  className={`mde__int-item${intSelected?._id === item._id ? " mde__int-item--selected" : ""}`}
                  onClick={() => {
                    setIntSelected(item);
                    if (!intDisplayText) setIntDisplayText(item.name || "");
                  }}
                >
                  <div className="mde__int-title">{item.name}</div>
                  {item.keywords?.length > 0 && (
                    <div className="mde__int-keywords">
                      {item.keywords.slice(0, 6).join(", ")}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <TextField
            label="Display text"
            value={intDisplayText}
            onChange={(e) => setIntDisplayText(e.target.value)}
            fullWidth
            size="small"
            placeholder="Defaults to item name"
          />

          <div className="mde__int-variant-row">
            <Typography variant="caption" sx={{ mr: 1, alignSelf: "center" }}>
              Style:
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={intVariant}
              onChange={(_, v) => v && setIntVariant(v)}
            >
              <ToggleButton value="regular">Regular</ToggleButton>
              <ToggleButton value="subtle">Subtle</ToggleButton>
              <ToggleButton value="italic">
                <em>Italic</em>
              </ToggleButton>
              <ToggleButton value="subtle-italic">
                <em>Subtle+Italic</em>
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          <Typography variant="caption" color="text.secondary">
            {intSelected
              ? `→ [${intDisplayText || intSelected.name}](https://${
                  {
                    regular: "internal-link",
                    subtle: "internal-link2",
                    italic: "internal-link-i",
                    "subtle-italic": "internal-link2-i",
                  }[intVariant]
                }/${intSelected._id})`
              : "Select an item above to see the generated link"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyIntLink}
            disabled={!intSelected}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Content ref link dialog ──────────────────────────────── */}
      <Dialog
        open={refOpen}
        onClose={() => setRefOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insert Content Reference</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "16px !important",
          }}
        >
          <TextField
            label="Filter by label"
            value={refSearch}
            onChange={(e) => setRefSearch(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            placeholder="Start typing to filter…"
          />

          {refSelected && (
            <Chip
              label={`Selected: ${refSelected.label}`}
              onDelete={() => setRefSelected(null)}
              color="primary"
              size="small"
            />
          )}

          <div className="mde__int-list">
            {filteredLabels.length === 0 ? (
              <div className="mde__int-empty">No labels found</div>
            ) : (
              filteredLabels.map((item) => (
                <div
                  key={item.label}
                  className={`mde__int-item${refSelected?.label === item.label ? " mde__int-item--selected" : ""}`}
                  onClick={() => setRefSelected(item)}
                >
                  <div className="mde__int-title">{item.label}</div>
                  <div className="mde__int-keywords">
                    {item.type.replace("EDCOMP_", "").toLowerCase()}
                  </div>
                </div>
              ))
            )}
          </div>

          {refSelected && (
            <Typography variant="caption" color="text.secondary">
              → {`\\ref{${refSelected.label}}{0.0}`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyRefLink}
            disabled={!refSelected}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default MarkdownEditor;
